/* SoulBotanic ritual moments — carousel enhancement for the card scroller.
   Progressive enhancement: the section renders and scrolls natively without
   this file, and the card copy stays visible. On top of native scroll it
   adds: mouse drag with rubber-band overscroll past either end, release
   momentum, prev/next arrows that step one card with an eased animation, a
   draggable 1px progress bar (matching the collection carousel above), and —
   on touch — tap-to-reveal of each card's supporting line. The controls and
   the unfurl-on-interaction copy only engage once init() tags the root. */
(function () {
  'use strict';

  var REDUCE = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };
  /* Touch / no-hover pointers reveal copy on tap instead of on hover. */
  var COARSE = window.matchMedia
    ? window.matchMedia('(hover: none)')
    : { matches: false };

  var STEP_MS = 600;
  var DRAG_THRESHOLD = 4; /* px of mouse travel before a press becomes a drag */
  var MAX_FLING = 4;      /* px/ms cap on release velocity */
  var MAX_OVER = 120;     /* px the track can be pulled past either end */
  var SPRING_MS = 460;    /* ease back from an overscroll */

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function init(root) {
    if (root.dataset.sbRitualInit) return;
    root.dataset.sbRitualInit = 'true';

    var scroller = root.querySelector('[data-sb-ritual-scroller]');
    var track = root.querySelector('.sb-ritual__track');
    var prev = root.querySelector('[data-sb-ritual-prev]');
    var next = root.querySelector('[data-sb-ritual-next]');
    var bar = root.querySelector('[data-sb-ritual-scrollbar]');
    var drag = root.querySelector('[data-sb-ritual-drag]');
    if (!scroller || !track || !prev || !next || !bar || !drag) return;

    root.classList.add('is-enhanced');

    var raf = 0;            /* shared handle: step, momentum or spring-back */
    var pendingTarget = null; /* step animation destination, for rapid clicks */
    var overTx = 0;         /* px the track is pulled past an end (rubber band) */

    function stopMotion() {
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
      pendingTarget = null;
    }

    function applyOver() {
      if (overTx) {
        track.style.transform = 'translate3d(' + overTx + 'px,0,0)';
        track.style.willChange = 'transform';
      } else {
        track.style.transform = '';
        track.style.willChange = '';
      }
    }

    /* Diminishing-returns rubber band: the further you pull, the less it
       gives, asymptotic to MAX_OVER so the row never tears off the edge. */
    function rubber(d) {
      return (MAX_OVER * d) / (d + MAX_OVER);
    }

    function springBack() {
      if (!overTx) { applyOver(); return; }
      stopMotion();
      var from = overTx;
      var start = window.performance.now();
      function tick(now) {
        var t = Math.min(1, (now - start) / SPRING_MS);
        overTx = from * (1 - easeOutCubic(t));
        applyOver();
        if (t < 1) {
          raf = window.requestAnimationFrame(tick);
        } else {
          raf = 0;
          overTx = 0;
          applyOver();
        }
      }
      raf = window.requestAnimationFrame(tick);
    }

    function maxScroll() {
      return scroller.scrollWidth - scroller.clientWidth;
    }

    function step() {
      var cards = scroller.querySelectorAll('.sb-ritual__card');
      if (cards.length > 1) return cards[1].offsetLeft - cards[0].offsetLeft;
      return scroller.clientWidth;
    }

    function update() {
      var max = maxScroll();
      if (max <= 2) {
        root.classList.remove('has-overflow');
        return;
      }
      root.classList.add('has-overflow');
      prev.disabled = scroller.scrollLeft <= 2;
      next.disabled = scroller.scrollLeft >= max - 2;

      /* Progress bar: thumb width = viewport share of the row, offset = how
         far along the scroll we are. Mirrors the collection carousel. */
      var barW = bar.clientWidth;
      var dragW = Math.max(barW * (scroller.clientWidth / scroller.scrollWidth), 24);
      var x = (scroller.scrollLeft / max) * (barW - dragW);
      drag.style.width = dragW + 'px';
      drag.style.transform = 'translateX(' + x + 'px)';
    }

    function animateTo(target) {
      target = clamp(target, 0, maxScroll());
      stopMotion();
      if (REDUCE.matches) {
        scroller.scrollLeft = target;
        return;
      }
      pendingTarget = target;
      var from = scroller.scrollLeft;
      var start = window.performance.now();
      function tick(now) {
        var t = Math.min(1, (now - start) / STEP_MS);
        scroller.scrollLeft = from + (target - from) * easeOutCubic(t);
        if (t < 1) {
          raf = window.requestAnimationFrame(tick);
        } else {
          raf = 0;
          pendingTarget = null;
        }
      }
      raf = window.requestAnimationFrame(tick);
    }

    function go(dir) {
      var from = pendingTarget === null ? scroller.scrollLeft : pendingTarget;
      animateTo(from + dir * step());
    }

    prev.addEventListener('click', function () { go(-1); });
    next.addEventListener('click', function () { go(1); });

    /* Mouse drag-to-scroll. Touch is left to the browser's native gesture;
       a mouse press scrubs scrollLeft 1:1. Past either end the surplus turns
       into a rubber-band transform instead of a dead stop, and the release
       eases it back; in-bounds releases glide on momentum. */
    var dragging = false;
    var dragMoved = false;
    var dragStartX = 0;
    var dragStartScroll = 0;
    var lastX = 0;
    var lastT = 0;
    var velocity = 0; /* pointer px/ms, smoothed */

    function momentum(v) {
      v = clamp(v, -MAX_FLING, MAX_FLING);
      var last = window.performance.now();
      function tick(now) {
        var dt = now - last;
        last = now;
        var max = maxScroll();
        scroller.scrollLeft += v * dt;
        v *= Math.pow(0.95, dt / 16.7);
        var x = scroller.scrollLeft;
        /* Ran into an end with speed to spare: spend it on a short bounce. */
        if ((x <= 0 && v < 0) || (x >= max && v > 0)) {
          overTx = clamp(-v * 14, -MAX_OVER, MAX_OVER);
          raf = 0;
          springBack();
          return;
        }
        if (Math.abs(v) < 0.02 || x <= 0 || x >= max) {
          raf = 0;
          return;
        }
        raf = window.requestAnimationFrame(tick);
      }
      raf = window.requestAnimationFrame(tick);
    }

    scroller.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      stopMotion();
      dragging = true;
      dragMoved = false;
      dragStartX = event.clientX;
      dragStartScroll = scroller.scrollLeft;
      lastX = event.clientX;
      lastT = event.timeStamp;
      velocity = 0;
      /* Blocks text selection and lets a clean click still reach the card. */
      event.preventDefault();
      if (scroller.setPointerCapture) scroller.setPointerCapture(event.pointerId);
    });

    scroller.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      var dx = event.clientX - dragStartX;
      if (!dragMoved && Math.abs(dx) < DRAG_THRESHOLD) return;
      dragMoved = true;
      var target = dragStartScroll - dx;
      var max = maxScroll();
      if (REDUCE.matches) {
        scroller.scrollLeft = clamp(target, 0, max);
      } else if (target < 0) {
        scroller.scrollLeft = 0;
        overTx = rubber(-target);
      } else if (target > max) {
        scroller.scrollLeft = max;
        overTx = -rubber(target - max);
      } else {
        scroller.scrollLeft = target;
        overTx = 0;
      }
      applyOver();
      var dt = event.timeStamp - lastT;
      if (dt > 0) {
        var v = (event.clientX - lastX) / dt;
        velocity = velocity * 0.2 + v * 0.8;
      }
      lastX = event.clientX;
      lastT = event.timeStamp;
    });

    function endDrag(event) {
      if (!dragging) return;
      dragging = false;
      if (scroller.hasPointerCapture && scroller.hasPointerCapture(event.pointerId)) {
        scroller.releasePointerCapture(event.pointerId);
      }
      /* Pulled past an end: ease the rubber band home, no competing fling. */
      if (overTx) {
        springBack();
        return;
      }
      /* A stale sample means the pointer stopped before release: no fling. */
      if (event.timeStamp - lastT > 80) velocity = 0;
      if (dragMoved && !REDUCE.matches && Math.abs(velocity) > 0.1) {
        momentum(-velocity);
      }
    }
    scroller.addEventListener('pointerup', endDrag);
    scroller.addEventListener('pointercancel', endDrag);

    /* A drag that ends over a card must not fire the card's link. */
    scroller.addEventListener('click', function (event) {
      if (!dragMoved) return;
      dragMoved = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    /* Hand control back to native scrolling the moment the wheel is used. */
    scroller.addEventListener('wheel', function () {
      stopMotion();
      if (overTx) { overTx = 0; applyOver(); }
    }, { passive: true });

    /* Progress bar: click jumps, pointer drag seeks. It sits outside the
       scroller, so its pointer stream never crosses the card-drag logic. */
    var barDragging = false;
    function seek(clientX) {
      var rect = bar.getBoundingClientRect();
      var dragW = drag.clientWidth;
      var usable = rect.width - dragW;
      if (usable <= 0) return;
      var fraction = clamp((clientX - rect.left - dragW / 2) / usable, 0, 1);
      stopMotion();
      if (overTx) { overTx = 0; applyOver(); }
      scroller.scrollLeft = fraction * maxScroll();
    }
    bar.addEventListener('pointerdown', function (event) {
      if (!root.classList.contains('has-overflow')) return;
      barDragging = true;
      try { bar.setPointerCapture(event.pointerId); } catch (error) { /* old engines */ }
      seek(event.clientX);
      event.preventDefault();
    });
    bar.addEventListener('pointermove', function (event) {
      if (barDragging) seek(event.clientX);
    });
    function endBarDrag() { barDragging = false; }
    bar.addEventListener('pointerup', endBarDrag);
    bar.addEventListener('pointercancel', endBarDrag);

    /* Touch reveal: a first tap on a closed card opens its copy (and is
       swallowed so an optional card link does not fire yet); a second tap
       follows the link, or closes the card when it has none. A tap anywhere
       outside the cards closes whatever is open. Hover-capable pointers use
       CSS :hover and are left untouched here. */
    var cards = Array.prototype.slice.call(root.querySelectorAll('.sb-ritual__card'));
    function closeAll() {
      for (var i = 0; i < cards.length; i++) cards[i].classList.remove('is-open');
    }
    cards.forEach(function (card) {
      card.addEventListener('click', function (event) {
        if (!COARSE.matches || dragMoved) return;
        var hasLink = !!card.querySelector('.sb-ritual__link');
        if (!card.classList.contains('is-open')) {
          closeAll();
          card.classList.add('is-open');
          event.preventDefault();
          event.stopPropagation();
        } else if (!hasLink) {
          card.classList.remove('is-open');
          event.preventDefault();
          event.stopPropagation();
        }
        /* open + has link: let the tap through to navigate. */
      }, true);
    });
    document.addEventListener('click', function (event) {
      if (!COARSE.matches) return;
      var inCard = event.target.closest ? event.target.closest('.sb-ritual__card') : null;
      if (inCard && root.contains(inCard)) return;
      closeAll();
    });

    /* ----- Uniform title fit ---------------------------------------------
       The moment-title's size (4rem desktop / 2.4rem mobile) was set on the
       English copy, where every title holds one line. Translations run longer
       (DE/NL/FR/BG), and at a fixed size the long ones break into two or
       three stacked lines of display type: their caption bars grow and those
       entries read as BIGGER type than their neighbours. One size must hold
       for every entry in whatever language is showing, so this measures each
       title's single-line width and shares the one scale that lets the
       longest title hold a single line (via --sb-ritual-name-scale, consumed
       by the section's font-size calc). English measures as fitting and
       keeps the full size. The floor sits below the worst live translation
       (NL ≈ 0.62) so a future copy edit degrades to wrapping, never to
       unreadable type. */
    var NAME_FIT_FLOOR = 0.55;
    var names = Array.prototype.slice.call(root.querySelectorAll('.sb-ritual__name'));
    function widestOverflow() {
      /* Subpixel measurement via a Range — scrollWidth clamps to clientWidth
         and truncates, so a title overflowing by a fraction of a px reads as
         "fits" and then wraps anyway. The fitted width targets 1px inside
         the box so a rounding hairline can never wrap the very title the
         scale was computed for. */
      var range = document.createRange();
      var worst = 1;
      for (var i = 0; i < names.length; i++) {
        var name = names[i];
        name.style.whiteSpace = 'nowrap';
        range.selectNodeContents(name);
        var needed = range.getBoundingClientRect().width;
        var avail = name.clientWidth - 1;
        name.style.whiteSpace = '';
        if (needed > avail && avail > 0) worst = Math.min(worst, avail / needed);
      }
      range.detach();
      return worst;
    }
    function fitNames() {
      if (!names.length) return;
      /* Back to the stylesheet size first, so re-runs (resize, breakpoint
         crossings) measure from the same base and never compound. */
      root.style.removeProperty('--sb-ritual-name-scale');
      /* The serif is a variable font with optical sizing: as the size drops
         it swaps toward a wider text-optical instance, so line width is NOT
         linear in font-size and a single computed ratio lands a few px
         short. Apply, re-measure at the applied size, repeat — converges in
         two or three passes. */
      var scale = 1;
      for (var pass = 0; pass < 4; pass++) {
        var worst = widestOverflow();
        if (worst >= 1) break;
        scale = Math.max(scale * worst, NAME_FIT_FLOOR);
        root.style.setProperty('--sb-ritual-name-scale', scale.toFixed(3));
        if (scale <= NAME_FIT_FLOOR) break;
      }
    }
    fitNames();
    /* Webfont metrics land after first paint; refit once they're real. */
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(fitNames);
    }

    var ticking = false;
    scroller.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    });
    window.addEventListener('resize', function () {
      fitNames();
      update();
    });

    /* ----- Scroll-linked drift -------------------------------------------
       Vertical page scroll nudges the row sideways and eases to a stop once
       the page stops. This row drifts RIGHT as you scroll down — opposite to
       the product carousel above, which goes left — so it starts at the far
       right and reveals earlier cards as you go. Implemented as a draining
       reservoir added to scrollLeft (never an absolute target), so a mouse
       drag, the arrows and the progress bar still move the row directly
       without the drift fighting or snapping them back. Active only while the
       section is in view; honors reduced-motion by staying off. */
    var DRIFT_FACTOR = -0.10; /* horizontal px per vertical px; negative = rightward — subtle */
    var DRIFT_EASE = 0.07;    /* share of the reservoir paid out per frame — gentle settle */
    var DRIFT_CAP = 280;      /* max px held, so a jump can't whip the row */
    var driftPending = 0;
    var driftRaf = 0;
    var driftInView = !('IntersectionObserver' in window);
    var lastPageY = window.pageYOffset || 0;

    function driftTick() {
      var max = maxScroll();
      if (max <= 2 || Math.abs(driftPending) < 0.5) {
        driftPending = 0;
        driftRaf = 0;
        return;
      }
      var consume = driftPending * DRIFT_EASE;
      scroller.scrollLeft = clamp(scroller.scrollLeft + consume, 0, max);
      driftPending -= consume;
      /* At an end with more to give: drop it so nothing banks up to snap the
         row back the moment the scroll direction reverses. */
      if ((scroller.scrollLeft <= 0.5 && driftPending < 0) ||
          (scroller.scrollLeft >= max - 0.5 && driftPending > 0)) {
        driftPending = 0;
      }
      driftRaf = window.requestAnimationFrame(driftTick);
    }

    function onPageScroll() {
      var y = window.pageYOffset || 0;
      var dy = y - lastPageY;
      lastPageY = y;
      if (!driftInView || dragging || !dy) return;
      driftPending = clamp(driftPending + dy * DRIFT_FACTOR, -DRIFT_CAP, DRIFT_CAP);
      if (!driftRaf) driftRaf = window.requestAnimationFrame(driftTick);
    }

    if (!REDUCE.matches) {
      scroller.scrollLeft = maxScroll(); /* begin at the far right to drift rightward */
      window.addEventListener('scroll', onPageScroll, { passive: true });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          driftInView = entries[0].isIntersecting;
          if (!driftInView) driftPending = 0;
        }, { threshold: 0 }).observe(root);
      }
    }

    update();
  }

  function initAll(scope) {
    var roots = scope.querySelectorAll('[data-sb-ritual]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
})();
